import { useEffect } from 'react';
import { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useSnackbar } from 'notistack';
import { IResponse, useStores } from 'src/models';

import { TApiResponse } from './apiTypes';
import instance from './axios';
/**
 * # axios 인스턴스를 매개변수로 받는 useAxiosInterceptor 컴스텀 훅
 * - 요청과 응답 인터셉터를 설정하고 컴포넌트가 언마운트될 때 제거한다.
 */
const useAxiosInterceptor = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { authStore, responseStore } = useStores();
  // 요청을 처리하는 로직
  const handleRequest = (config: InternalAxiosRequestConfig<any>) => {
    return config;
  };

  // 응답을 처리하는 로직
  const handleResponse = async (response: TApiResponse<any>) => {
    // 파일 다운로드 처리: Blob 데이터 반환
    if (response.data instanceof Blob) {
      return response;
    }

    // 초기화 후 응답을 저장: 성공 또는 실패 시 공통적으로 responseStore 업데이트
    await responseStore.responseInfo.reset();
    await responseStore.setResponseInfo(response.data as IResponse);

    // 성공 처리
    if (response.data.resultCode === 'S') {
      return response; // 성공 응답 반환
    }

    // 실패 처리: resultCode가 'F'인 경우 에러 처리, resultCode가 'S'이면서 에러일 경우 포함
    switch (response.data.errorCode) {
      // 인증 에러 처리
      case '401':
      case '4001':
        enqueueSnackbar('인증 에러가 발생했습니다.', { variant: 'error' });
        authStore.initAuth();
        break;
      case '400':
        enqueueSnackbar(response.data.errorMessage, { variant: 'error' });
        break;
      case '500':
        enqueueSnackbar('서버 에러가 발생했습니다.', { variant: 'error' });
        break;
      default:
      // 기타 에러는 `responseStore`에 저장만 수행 -> 에러 표시는 각 화면에서 처리(useHandleResponse 사용)
    }
    return Promise.reject(response);
  };

  // 에러를 처리하는 로직
  const handleError = (error: AxiosError): Promise<AxiosError> => {
    // 에러 알림 처리
    responseStore.errorProcessing(error);
    enqueueSnackbar('네트워크 오류가 발생했습니다.', { variant: 'error' });
    return Promise.reject(error);
  };

  // 인터셉터를 설정
  const setupInterceptors = (): { requestInterceptor: number; responseInterceptor: number } => {
    const requestInterceptor = instance.interceptors.request.use(handleRequest, handleError);
    const responseInterceptor = instance.interceptors.response.use(handleResponse, handleError);

    return { requestInterceptor, responseInterceptor };
  };

  // 인터셉터를 제거
  const ejectInterceptors = (requestInterceptor: number, responseInterceptor: number): void => {
    instance.interceptors.request.eject(requestInterceptor);
    instance.interceptors.response.eject(responseInterceptor);
  };

  // 컴포넌트가 마운트될 때 인터셉터를 설정하고, 언마운트될 때 인터셉터를 제거한다.
  useEffect(() => {
    const { requestInterceptor, responseInterceptor } = setupInterceptors();
    return () => {
      ejectInterceptors(requestInterceptor, responseInterceptor);
    };
  }, []);
};

/**
 * @title AxiosInterceptor
 * @description Axios 인터셉터를 설정하는 컴포넌트
 */
export const AxiosInterceptor = () => {
  useAxiosInterceptor();
  return null;
};
